import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  DollarSign,
  CreditCard,
  Calculator,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  FileText,
  Eye,
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Bookmark,
  Star,
  Share,
  Info,
  ExternalLink,
  Archive,
  Send,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Upload,
  FileUp,
  FileDown,
  FileCheck,
  FileX,
  Volume2,
  VolumeX,
  Headphones,
  Speaker,
  Radio,
  Music,
  Video,
  Camera,
  Image,
  File,
  Folder,
  FolderOpen,
  HardDrive,
  Cloud,
  Wifi,
  WifiOff,
  Signal,
  Battery,
  BatteryLow,
  Power,
  PowerOff,
  Activity,
  Zap,
  Shield,
  Lock,
  Key,
  Users,
  Building,
  Calendar,
  Phone,
  Mail,
  MapPin,
  CheckSquare,
  Square as SquareIcon,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Settings,
  Link,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  Database,
  Layers,
  User,
  Languages,
  Mic,
  Play,
  Pause,
  Square,
  RefreshCw,
  Copy,
  Download,
  Globe,
  Users2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Target,
  Brain,
  MessageSquare,
  AlertCircle,
  CheckSquare as CheckSquareIcon,
  HelpCircle,
  Package,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast } from '../common/Toast';
import { contractFinanceService } from '../../services/contractFinanceService';
import ContractService from '../../services/contractService';

/**
 * CONTRACT FINANCE – AUTHORITATIVE BILLING MODULE
 * -----------------------------------------------
 * This component is the primary UI for managing contract‑level:
 *   - payments (CRUD via /finance/contracts/payments[/:paymentId])
 *   - invoices (via /finance/contracts/invoices)
 *   - analytics and revenue recognition.
 *
 * The subcontracting view `subcontracting/BillingPayments` shows a
 * narrower, operational list of the SAME payments using the same
 * `contractFinanceService`. That view is intentionally kept lighter
 * so we only have ONE rich form (this file) sending payloads to the
 * shared backend routes, avoiding two different form structures for
 * the same contract‑billing APIs.
 */

// Custom Dropdown Component for Payment Actions
const PaymentActionDropdown = ({ payment, onProcessPayment, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const idRef = useRef(`action-btn-${Math.random().toString(36).slice(2, 9)}`);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on scroll/resize/touch to avoid mis-positioned fixed menus
  useEffect(() => {
    if (!isOpen) return undefined;
    const close = () => setIsOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('touchstart', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('touchstart', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 250;
      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen((s) => !s);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  const handleProcessPayment = () => {
    setIsOpen(false);
    if (payment && (payment.id || payment._id)) {
      onProcessPayment(payment);
    }
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (payment && (payment.id || payment._id)) {
      onDelete(payment);
    }
  };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative' }}
    >
      <button
        id={idRef.current}
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        title="Actions"
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#E8F1FD';
          e.currentTarget.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#ffffff';
          e.currentTarget.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>

      {isOpen && buttonRef.current && (
        <ul
          role="menu"
          aria-labelledby={idRef.current}
          className="dropdown-menu dropdown-menu-end show"
          style={{
            position: 'fixed',
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            right: `${window.innerWidth - buttonRef.current.getBoundingClientRect().right}px`,
            zIndex: 100000,
            minWidth: '180px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '4px 0',
            backgroundColor: 'white',
            listStyle: 'none',
            margin: 0,
          }}
        >
          {payment?.status !== 'Paid' && (
            <li role="none">
              <button
                role="menuitem"
                className="dropdown-item d-flex align-items-center"
                onClick={handleProcessPayment}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  color: '#374151',
                  border: 'none',
                  backgroundColor: 'transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CreditCard
                  size={16}
                  style={{
                    marginRight: '10px',
                    color: '#6b7280',
                    flexShrink: 0,
                  }}
                />
                Process Payment
              </button>
            </li>
          )}

          <li
            role="none"
            style={{
              borderTop: '1px solid #e5e7eb',
              marginTop: '4px',
              paddingTop: '4px',
            }}
          >
            <button
              role="menuitem"
              className="dropdown-item d-flex align-items-center"
              onClick={handleDelete}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#dc2626',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fef2f2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Trash2
                size={16}
                style={{ marginRight: '10px', color: '#dc2626', flexShrink: 0 }}
              />
              Delete Payment
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

// Custom Dropdown Component for Invoice Actions
const InvoiceActionDropdown = ({
  invoice,
  onView,
  onEdit,
  onSendInvoice,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const idRef = useRef(
    `invoice-action-btn-${Math.random().toString(36).slice(2, 9)}`,
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const close = () => setIsOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('touchstart', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('touchstart', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 250;
      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen((s) => !s);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  const handleView = () => {
    setIsOpen(false);
    if (invoice && (invoice.id || invoice._id || invoice.invoiceId)) {
      onView(invoice);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (invoice && (invoice.id || invoice._id || invoice.invoiceId)) {
      onEdit(invoice);
    }
  };

  const handleSendInvoice = () => {
    setIsOpen(false);
    if (invoice && (invoice.id || invoice._id || invoice.invoiceId)) {
      onSendInvoice(invoice);
    }
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (invoice && (invoice.id || invoice._id || invoice.invoiceId)) {
      onDelete(invoice);
    }
  };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative' }}
    >
      <button
        id={idRef.current}
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        title="Invoice actions"
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#E8F1FD';
          e.currentTarget.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#ffffff';
          e.currentTarget.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>

      {isOpen && buttonRef.current && (
        <ul
          role="menu"
          aria-labelledby={idRef.current}
          className="dropdown-menu dropdown-menu-end show"
          style={{
            position: 'fixed',
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            right: `${window.innerWidth - buttonRef.current.getBoundingClientRect().right}px`,
            zIndex: 100000,
            minWidth: '180px',
            boxShadow: '0 8px 20px rgba(0,0,0,0.12)',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '4px 0',
            backgroundColor: 'white',
            listStyle: 'none',
            margin: 0,
          }}
        >
          <li role="none">
            <button
              role="menuitem"
              className="dropdown-item d-flex align-items-center"
              onClick={handleView}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Eye
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              View Invoice
            </button>
          </li>

          <li role="none">
            <button
              role="menuitem"
              className="dropdown-item d-flex align-items-center"
              onClick={handleEdit}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Edit
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              Edit Invoice
            </button>
          </li>

          {invoice?.status === 'Draft' && (
            <li role="none">
              <button
                role="menuitem"
                className="dropdown-item d-flex align-items-center"
                onClick={handleSendInvoice}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  color: '#374151',
                  border: 'none',
                  backgroundColor: 'transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Send
                  size={16}
                  style={{
                    marginRight: '10px',
                    color: '#6b7280',
                    flexShrink: 0,
                  }}
                />
                Send Invoice
              </button>
            </li>
          )}

          <li
            role="none"
            style={{
              borderTop: '1px solid #e5e7eb',
              marginTop: '4px',
              paddingTop: '4px',
            }}
          >
            <button
              role="menuitem"
              className="dropdown-item d-flex align-items-center"
              onClick={handleDelete}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#dc2626',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fef2f2';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Trash2
                size={16}
                style={{ marginRight: '10px', color: '#dc2626', flexShrink: 0 }}
              />
              Delete Invoice
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

const ContractFinance = () => {
  const { success, error: showError } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('payments');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [contractPayments, setContractPayments] = useState([]);
  const [contractInvoices, setContractInvoices] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState('');

  const [paymentTotals, setPaymentTotals] = useState({
    totalPaid: 0,
    outstanding: 0,
  });

  const [selectedContractIndex, setSelectedContractIndex] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    paymentType: 'One-Time',
    amount: '',
    dueDate: '',
    status: 'Pending',
    paymentMethod: '',
    invoiceNumber: '',
    reference: '',
    notes: '',
  });

  const stats = {
    totalPayments: analytics?.totalPayments || 0,
    pendingPayments: analytics?.pendingPayments || 0,
    pendingAmount: analytics?.pendingAmount || 0,
    overduePayments: analytics?.overduePayments || 0,
    paidPayments: analytics?.paidPayments || 0,
    totalAmount: analytics?.totalAmount || 0,
    totalInvoices: contractInvoices.length,
  };

  const tabs = [
    { id: 'payments', label: 'Contract Payments' },
    { id: 'invoices', label: 'Contract Invoices' },
    { id: 'analytics', label: 'Financial Analytics' },
    { id: 'reports', label: 'Financial Reports' },
    { id: 'settings', label: 'Payment Settings' },
  ];

  const formatCurrency = (value, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      value || 0,
    );

  const loadContracts = async () => {
    try {
      const response = await ContractService.getContracts();
      if (response.success) {
        let contractsList = response.data?.contracts || response.data || [];
        contractsList = contractsList.map((contract) => {
          const normalizedId =
            contract._id || contract.id || contract.contractId || '';
          const statusLower = String(contract.status || '').toLowerCase();
          const readOnly =
            contract.isActive === false ||
            ['cancelled', 'terminated', 'expired'].includes(statusLower);
          return {
            ...contract,
            _id: normalizedId,
            readOnly,
          };
        });
        setContracts(contractsList);
        if (!selectedContractId && contractsList.length) {
          setSelectedContractIndex(0);
          setSelectedContractId(contractsList[0]._id);
        }
      }
    } catch (error) {
      showError(error.message || 'Failed to load contracts');
    }
  };

  const loadPayments = async (contractId) => {
    if (!contractId) {
      setContractPayments([]);
      setPaymentTotals({ totalPaid: 0, outstanding: 0 });
      return;
    }
    try {
      setLoading(true);
      const response =
        await contractFinanceService.getContractPayments(contractId);
      if (response.success) {
        setContractPayments(response.data.payments || []);
        setPaymentTotals({
          totalPaid: response.data.totalPaid || 0,
          outstanding: response.data.outstanding || 0,
        });
      }
    } catch (error) {
      showError(error.message || 'Failed to load contract payments');
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async (contractId) => {
    if (!contractId) {
      setContractInvoices([]);
      return;
    }
    try {
      setLoading(true);
      const response =
        await contractFinanceService.getContractInvoices(contractId);
      if (response.success) {
        setContractInvoices(response.data || []);
      }
    } catch (error) {
      showError(error.message || 'Failed to load contract invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await contractFinanceService.getAnalytics();
      if (response.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      showError(error.message || 'Failed to load contract finance analytics');
    }
  };

  useEffect(() => {
    loadContracts();
    loadAnalytics();
  }, []);

  useEffect(() => {
    loadPayments(selectedContractId);
    loadInvoices(selectedContractId);
  }, [selectedContractId]);

  const selectedContract =
    selectedContractIndex !== null ? contracts[selectedContractIndex] : null;
  const isSelectedContractReadOnly = Boolean(selectedContract?.readOnly);

  // Define columns for Contract Payments DataTable
  const paymentColumns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'contractTitle',
      label: 'Contract & Counterparty',
      width: '280px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.paymentType} • {row.counterparty}
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      width: '120px',
      render: (amount) => (
        <span style={{ fontWeight: '600', color: '#1f2937' }}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => {
        const statusConfig = {
          Pending: {
            color: '#f59e0b',
            bg: '#fef3c7',
            icon: <Clock size={14} />,
          },
          Overdue: {
            color: '#ef4444',
            bg: '#fee2e2',
            icon: <AlertTriangle size={14} />,
          },
          Paid: {
            color: '#10b981',
            bg: '#d1fae5',
            icon: <CheckCircle size={14} />,
          },
          Cancelled: {
            color: '#6b7280',
            bg: '#f3f4f6',
            icon: <XCircle size={14} />,
          },
        };
        const config = statusConfig[status] || statusConfig.Pending;
        return (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: config.bg,
              color: config.color,
              width: 'fit-content',
            }}
          >
            {config.icon}
            {status}
          </span>
        );
      },
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      width: '120px',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '—'),
    },
    {
      key: 'paymentMethod',
      label: 'Payment Method',
      width: '130px',
      render: (method) => (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          {method || '—'}
        </span>
      ),
    },
    {
      key: 'invoiceNumber',
      label: 'Invoice #',
      width: '120px',
      render: (invoiceNumber) => (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          {invoiceNumber || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => (
        <PaymentActionDropdown
          payment={row}
          onProcessPayment={handleProcessPayment}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  // Define columns for Contract Invoices DataTable
  const invoiceColumns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />,
    },
    {
      key: 'invoiceNumber',
      label: 'Invoice',
      width: '220px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.contractTitle} • {row.counterparty}
          </div>
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      width: '120px',
      render: (amount) => (
        <span style={{ fontWeight: '600', color: '#1f2937' }}>
          {formatCurrency(amount)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => {
        const statusConfig = {
          Pending: {
            color: '#f59e0b',
            bg: '#fef3c7',
            icon: <Clock size={14} />,
          },
          Paid: {
            color: '#10b981',
            bg: '#d1fae5',
            icon: <CheckCircle size={14} />,
          },
          Overdue: {
            color: '#ef4444',
            bg: '#fee2e2',
            icon: <AlertTriangle size={14} />,
          },
          Cancelled: {
            color: '#6b7280',
            bg: '#f3f4f6',
            icon: <XCircle size={14} />,
          },
        };
        const config = statusConfig[status] || statusConfig.Pending;
        return (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: config.bg,
              color: config.color,
              width: 'fit-content',
            }}
          >
            {config.icon}
            {status}
          </span>
        );
      },
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      width: '120px',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '—'),
    },
    {
      key: 'paymentMethod',
      label: 'Payment Method',
      width: '130px',
      render: (method) => (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          {method || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => (
        <InvoiceActionDropdown
          invoice={row}
          onView={handleViewInvoice}
          onEdit={handleEditInvoice}
          onSendInvoice={handleSendInvoice}
          onDelete={handleDeleteInvoice}
        />
      ),
    },
  ];

  const handleProcessPayment = (payment) => {
    setSelectedItem(payment);
    setShowPaymentModal(true);
  };

  const handleDelete = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const handleViewInvoice = (invoice) => {
    // Navigate to the invoice view page if an id is available, otherwise store selection
    console.log('View invoice:', invoice);
    const id = invoice?.id || invoice?._id || invoice?.invoiceId;
    if (id) {
      navigate(`/projects/time-tracking/billing/view/${id}`, {
        state: { invoice },
      });
      return;
    }
    setSelectedItem(invoice);
    success('Viewing invoice details');
  };

  const handleEditInvoice = (invoice) => {
    console.log('Edit invoice:', invoice);
    const id = invoice?.id || invoice?._id || invoice?.invoiceId;
    if (id) {
      navigate(`/projects/time-tracking/billing/edit/${id}`, {
        state: { invoice },
      });
      return;
    }
    // Fallback: store selection and show toast
    setSelectedItem(invoice);
    success('Opening invoice editor');
  };

  const handleSendInvoice = (invoice) => {
    // Send invoice via email
    console.log('Send invoice:', invoice);
    success('Sending invoice...');
  };

  const handleDeleteInvoice = (invoice) => {
    setSelectedItem(invoice);
    setShowDeleteModal(true);
  };

  const handleRefresh = () => {
    loadPayments(selectedContractId);
    loadInvoices(selectedContractId);
    loadAnalytics();
  };

  const handleHelpClick = () => {
    const helpUrl = '/help/contract-finance';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no',
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  const handleCreatePayment = () => {
    const contract = contracts[selectedContractIndex];
    if (!selectedContractId || !contract) {
      showError('Select a contract before creating a payment');
      return;
    }
    if (contract.readOnly) {
      showError('Cannot create payments for terminated or expired contracts');
      return;
    }
    setPaymentFormData({
      paymentType: 'One-Time',
      amount: '',
      dueDate: '',
      status: 'Pending',
      paymentMethod: '',
      invoiceNumber: '',
      reference: '',
      notes: '',
    });
    setShowPaymentForm(true);
  };

  const handlePaymentFormSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    try {
      const payload = {
        paymentType: paymentFormData.paymentType,
        amount: Number(paymentFormData.amount),
        dueDate: paymentFormData.dueDate,
        status: paymentFormData.status,
        paymentMethod: paymentFormData.paymentMethod,
        invoiceNumber: paymentFormData.invoiceNumber,
        reference: paymentFormData.reference,
        notes: paymentFormData.notes,
      };
      await contractFinanceService.createContractPayment(
        selectedContractId,
        payload,
      );
      success('Contract payment created successfully');
      setShowPaymentForm(false);
      await loadPayments(selectedContractId);
      await loadAnalytics();
    } catch (error) {
      showError(error.message || 'Failed to create payment');
    } finally {
      setIsProcessing(false);
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
      disabled: loading,
    },
    ...(activeTab === 'payments'
      ? [
          {
            label: 'New Payment',
            icon: <Plus size={18} />,
            onClick: handleCreatePayment,
            variant: 'primary',
            tooltip: 'Create a new payment',
            disabled:
              loading || !selectedContractId || isSelectedContractReadOnly,
          },
        ]
      : []),
  ];

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (activeTab === 'payments' && selectedItem?._id) {
        await contractFinanceService.deleteContractPayment(selectedItem._id);
        await loadPayments(selectedContractId);
        success('Contract payment deleted successfully');
      }
      setShowDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      showError(error.message || 'Failed to delete item');
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePaymentConfirm = async () => {
    setIsProcessing(true);
    try {
      if (selectedItem?._id) {
        await contractFinanceService.updateContractPayment(selectedItem._id, {
          status: 'Paid',
        });
        await loadPayments(selectedContractId);
        await loadAnalytics();
        success('Payment marked as paid');
      }
      setShowPaymentModal(false);
      setSelectedItem(null);
    } catch (error) {
      showError(error.message || 'Failed to process payment');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Contract Finance"
        description="Comprehensive financial management for contract payments, invoicing, and financial tracking"
        actions={headerActions}
      />
      <div className="page-container">
        <div
          style={{
            marginBottom: '20px',
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
          }}
        >
          <label style={{ fontWeight: 500, color: '#374151' }}>
            Select Contract
          </label>
          <select
            value={selectedContractIndex ?? ''}
            onChange={(event) => {
              const index =
                event.target.value === '' ? null : Number(event.target.value);
              if (index === null || Number.isNaN(index)) {
                setSelectedContractIndex(null);
                setSelectedContractId('');
                return;
              }
              const contract = contracts[index];
              const resolvedContractId =
                contract?._id || contract?.id || contract?.contractId || '';
              setSelectedContractIndex(index);
              setSelectedContractId(resolvedContractId);
            }}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              minWidth: '280px',
            }}
          >
            <option value="">Choose a contract</option>
            {contracts.map((contract, index) => (
              <option key={contract._id || contract.id || index} value={index}>
                {contract.title} • {contract.counterparty}
              </option>
            ))}
          </select>
          {selectedContract?.readOnly && (
            <div
              style={{ fontSize: '12px', color: '#dc2626', marginLeft: '12px' }}
            >
              Selected contract is terminated/expired; payments are disabled.
            </div>
          )}
        </div>

        {/* KPI Cards Grid – StatCard reference styling */}
        <div className="grid-modern-4 w-full gap-6 mb-6">
          {/* Pending Payments */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              {/* Status – top right */}
              <div className="absolute right-4 top-3 text-[11px] font-semibold px-2 py-1 rounded bg-[#FEF3C7] text-[#92400E]">
                Action Required
              </div>

              {/* Icon */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Clock size={20} style={{ color: '#2D7DD2' }} />
              </div>

              {/* Text */}
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.pendingPayments}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Pending Payments
                </div>
              </div>
            </div>
          </div>

          {/* Overdue Payments */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              {/* Status – top right */}
              <div className="absolute right-4 top-3 text-[11px] font-semibold px-2 py-1 rounded bg-[#FEE2E2] text-[#991B1B]">
                Urgent
              </div>

              {/* Icon */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <AlertTriangle size={20} style={{ color: '#2D7DD2' }} />
              </div>

              {/* Text */}
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.overduePayments}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Overdue Payments
                </div>
              </div>
            </div>
          </div>

          {/* Total Invoices */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              {/* Trend – top right */}
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} />
                {contractInvoices.length}
              </div>

              {/* Icon */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Receipt size={20} style={{ color: '#2D7DD2' }} />
              </div>

              {/* Text */}
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalInvoices}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Invoices
                </div>
              </div>
            </div>
          </div>

          {/* Total Amount */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              {/* Trend – top right */}
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} /> +12.5%
              </div>

              {/* Icon */}
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <DollarSign size={20} style={{ color: '#2D7DD2' }} />
              </div>

              {/* Text */}
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {formatCurrency(stats.totalAmount)}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Amount
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Navigation */}
        <div style={{ marginBottom: '32px' }}>
          <div className="flex bg-white border-b border-slate-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center justify-center gap-2 px-5 py-3 w-full
                    text-sm font-medium
                    border-b-2 transition-colors duration-200
                  ${
                    activeTab === tab.id
                      ? '!border-b-2 border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                      : 'border-transparent text-[var(--text-secondary)] hover:bg-slate-100 text-slate-500'
                  }
                `}
              >
                {tab.id === 'payments' && (
                  <CreditCard
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'invoices' && (
                  <Receipt
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'analytics' && (
                  <BarChart3
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'reports' && (
                  <FileText
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'settings' && (
                  <Settings
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Contract Payments Tab */}
        {activeTab === 'payments' && (
          <div>
            {/* Payments DataTable */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <DataTable
                data={contractPayments}
                columns={paymentColumns}
                title={`Contract Payments (${contractPayments.length} payments)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* Contract Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            {/* Invoices DataTable */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <DataTable
                data={contractInvoices}
                columns={invoiceColumns}
                title={`Contract Invoices (${contractInvoices.length} invoices)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* Other Tabs */}
        {activeTab === 'analytics' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
              }}
            >
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Total Payments
                </div>
                <div style={{ fontWeight: 600 }}>{stats.totalPayments}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Total Amount
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(stats.totalAmount)}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Pending Payments
                </div>
                <div style={{ fontWeight: 600 }}>{stats.pendingPayments}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Overdue Payments
                </div>
                <div style={{ fontWeight: 600 }}>{stats.overduePayments}</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reports' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
              }}
            >
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Selected Contract Paid
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(paymentTotals.totalPaid)}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Selected Contract Outstanding
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(paymentTotals.outstanding)}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Invoices
                </div>
                <div style={{ fontWeight: 600 }}>{contractInvoices.length}</div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Paid Payments
                </div>
                <div style={{ fontWeight: 600 }}>{stats.paidPayments}</div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              minHeight: '200px',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '16px',
              }}
            >
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Total Paid
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(paymentTotals.totalPaid)}
                </div>
              </div>
              <div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  Outstanding
                </div>
                <div style={{ fontWeight: 600 }}>
                  {formatCurrency(paymentTotals.outstanding)}
                </div>
              </div>
            </div>
          </div>
        )}

        {showPaymentForm && (
          <div
            className="fixed inset-0 z-[1050] flex items-center justify-center bg-black/50 p-5"
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setShowPaymentForm(false);
              }
            }}
          >
            <div className="w-full max-w-[520px] rounded-xl bg-white p-6 shadow-[0_10px_25px_rgba(0,0,0,0.2)]">
              <h3 className="mb-4 text-lg font-semibold">
                Create Contract Payment
              </h3>

              <form onSubmit={handlePaymentFormSubmit}>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {/* Payment Type */}
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Payment Type</span>
                    <select
                      value={paymentFormData.paymentType}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          paymentType: e.target.value,
                        })
                      }
                      className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                    >
                      <option value="Milestone">Milestone</option>
                      <option value="Recurring">Recurring</option>
                      <option value="One-Time">One-Time</option>
                      <option value="Renewal">Renewal</option>
                    </select>
                  </label>

                  {/* Amount */}
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Amount</span>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={paymentFormData.amount}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          amount: e.target.value,
                        })
                      }
                      className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                    />
                  </label>

                  {/* Due Date */}
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Due Date</span>
                    <input
                      type="date"
                      required
                      value={paymentFormData.dueDate}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          dueDate: e.target.value,
                        })
                      }
                      className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                    />
                  </label>

                  {/* Status */}
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Status</span>
                    <select
                      value={paymentFormData.status}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          status: e.target.value,
                        })
                      }
                      className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Paid">Paid</option>
                      <option value="Overdue">Overdue</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </label>

                  {/* Payment Method */}
                  <label className="flex flex-col gap-1.5 sm:col-span-2">
                    <span className="text-sm font-medium">Payment Method</span>
                    <input
                      type="text"
                      value={paymentFormData.paymentMethod}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          paymentMethod: e.target.value,
                        })
                      }
                      className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                    />
                  </label>

                  {/* Invoice Number */}
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Invoice Number</span>
                    <input
                      type="text"
                      value={paymentFormData.invoiceNumber}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          invoiceNumber: e.target.value,
                        })
                      }
                      className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                    />
                  </label>

                  {/* Reference */}
                  <label className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">Reference</span>
                    <input
                      type="text"
                      value={paymentFormData.reference}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          reference: e.target.value,
                        })
                      }
                      className="rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                    />
                  </label>

                  {/* Notes */}
                  <label className="flex flex-col gap-1.5 sm:col-span-2">
                    <span className="text-sm font-medium">Notes</span>
                    <textarea
                      rows={3}
                      value={paymentFormData.notes}
                      onChange={(e) =>
                        setPaymentFormData({
                          ...paymentFormData,
                          notes: e.target.value,
                        })
                      }
                      className="resize-none rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
                    />
                  </label>
                </div>

                {/* Actions */}
                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setShowPaymentForm(false)}
                    className="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="rounded-md bg-[var(--primary-color)] px-4 py-2 text-sm text-white disabled:opacity-60"
                  >
                    Create Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Item"
          message={`Are you sure you want to delete "${
            selectedItem?.contractTitle || selectedItem?.invoiceNumber
          }"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        {/* Payment Processing Modal */}
        <ConfirmationModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onConfirm={handlePaymentConfirm}
          title="Process Payment"
          message={`Process payment for "${
            selectedItem?.contractTitle
          }"? Amount: ${formatCurrency(selectedItem?.amount)}`}
          confirmText="Process Payment"
          cancelText="Cancel"
          type="success"
          isLoading={isProcessing}
        />
      </div>
    </>
  );
};

export default ContractFinance;
